import { useState } from 'react';
import { Box, Button, IconButton, TextField, Switch, Typography, CircularProgress } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Delete } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { axiosPrivate } from 'app/utils';
import { useSelectedPharmacy } from 'app/hooks';
import useGetBlockOutRanges from 'app/pages/Services/use-cases/BlockOutDays/hooks/useGetBlockOutRanges';
import organisationApi from 'apis/core/organisation';
import { useConfirm } from 'app/shared-components/modals/ConfirmProvider';

// The backend uses event sourcing with async projections. Mutations resolve before
// the read model is updated, so immediate refetches return stale data. We use
// optimistic updates with a delayed invalidation instead of the shared hooks
// (useCreateBlockOutRange/useDeleteBlockOutRange) which invalidate immediately.
const PROJECTION_DELAY = 1500;

const ClosureDays = () => {
  const selectedPharmacy = useSelectedPharmacy();
  const organisationId = selectedPharmacy?.chain_id;
  const pharmacyId = selectedPharmacy?._id;
  const [showForm, setShowForm] = useState(false);
  const [note, setNote] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [blockRefills, setBlockRefills] = useState(true);

  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const queryKey = ['block-out-ranges', organisationId, pharmacyId, 1];
  const { data, isLoading } = useGetBlockOutRanges({ organisationId, branchId: pharmacyId });

  const delayedInvalidate = () => {
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey });
    }, PROJECTION_DELAY);
  };

  const { mutate: createClosure, isLoading: isCreating } = useMutation({
    mutationFn: (params) => axiosPrivate.post('/v3/pharmacist/availability-block-out-ranges', params),
    meta: {
      successMessage: 'Closure created successfully',
      errorMessage: 'Failed to create closure',
    },
    onSuccess: delayedInvalidate,
    onError: () => {
      // Revert optimistic update on failure
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: deleteClosure, isLoading: isDeleting } = useMutation({
    mutationFn: ({ blockOutRangeId }) => organisationApi.deleteBlockOutRange({ blockOutRangeId }),
    meta: {
      successMessage: 'Closure deleted successfully',
      errorMessage: 'Failed to delete closure',
    },
    onSuccess: delayedInvalidate,
    onError: () => {
      // Revert optimistic update on failure
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const closures = data?.blockOutRanges || [];

  const resetForm = () => {
    setNote('');
    setStartDate(null);
    setEndDate(null);
    setBlockRefills(true);
    setShowForm(false);
  };

  const handleAdd = () => {
    if (!startDate || !endDate) return;

    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

    // Optimistic update
    const optimisticClosure = {
      id: uuidv4(),
      organisationId,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      serviceTemplateIds: [],
      branchIds: [pharmacyId],
      blockRefills,
      note: note || undefined,
    };

    queryClient.setQueryData(queryKey, (old) => ({
      ...old,
      blockOutRanges: [...(old?.blockOutRanges || []), optimisticClosure],
    }));

    createClosure({
      organisationId,
      branchIds: [pharmacyId],
      serviceTemplateIds: [],
      blockRefills,
      note: note || undefined,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });

    resetForm();
  };

  const handleDelete = async (closure) => {
    const confirmed = await confirm({
      title: 'Delete Closure',
      children: (
        <Typography>
          Are you sure you want to delete this closure
          {closure.note ? ` "${closure.note}"` : ''}?
        </Typography>
      ),
      confirmType: 'danger',
      confirmLabel: 'Delete',
    });

    if (confirmed) {
      // Optimistic update
      queryClient.setQueryData(queryKey, (old) => ({
        ...old,
        blockOutRanges: (old?.blockOutRanges || []).filter((c) => c.id !== closure.id),
      }));

      deleteClosure({ blockOutRangeId: closure.id });
    }
  };

  if (isLoading) {
    return (
      <Box className="flex justify-center py-16">
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box className="flex flex-col gap-16">
      {closures.map((closure) => (
        <Box key={closure.id} className="flex items-center gap-8 p-12 border border-neutral-300 rounded-4">
          <Box className="flex flex-col grow gap-4">
            {closure.note && (
              <Typography className="text-14 font-600">{closure.note}</Typography>
            )}
            <Typography className="text-14">
              {moment(closure.startDate).format('MMM D, YYYY')} – {moment(closure.endDate).format('MMM D, YYYY')}
            </Typography>
            {closure.blockRefills && (
              <Typography className="text-12 text-grey-40">Blocks refills</Typography>
            )}
          </Box>
          <IconButton color="error" onClick={() => handleDelete(closure)} disabled={isDeleting}>
            <Delete />
          </IconButton>
        </Box>
      ))}

      {showForm && (
        <Box className="flex flex-col gap-12 p-12 border border-neutral-300 rounded-4">
          <TextField
            label="Reason (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 500 }}
            fullWidth
            size="small"
          />
          <Box className="flex items-center gap-8">
            <DatePicker
              label="Start Date"
              value={startDate}
              minDate={new Date()}
              format="yyyy-MM-dd"
              onChange={setStartDate}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              minDate={startDate || new Date()}
              format="yyyy-MM-dd"
              onChange={setEndDate}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Box>
          <Box className="flex items-center gap-8">
            <Typography className="text-14">Block fills and refills</Typography>
            <Switch
              checked={blockRefills}
              onChange={(e) => setBlockRefills(e.target.checked)}
              size="small"
            />
          </Box>
          <Box className="flex gap-8">
            <Button
              variant="contained"
              size="small"
              onClick={handleAdd}
              disabled={!startDate || !endDate || isCreating}
              sx={{ textTransform: 'none' }}
            >
              {isCreating ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="small"
              onClick={resetForm}
              disabled={isCreating}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {!showForm && (
        <Button
          onClick={() => setShowForm(true)}
          className="text-black opacity-89 border border-neutral-300 rounded-4 px-16 py-8 text-14 font-600 normal-case"
        >
          Add closure
        </Button>
      )}
    </Box>
  );
};

export default ClosureDays;
